import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  ContractNature,
  Currency,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { useSub } from "../../../../../common/subscription/useSub";
import { useRunsPublicProcurements } from "../../../../../common/organization/useRunsPublicProcurements";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { CONTRACT_NATURE_OPTIONS } from "../utils/publicProcurementOptions";
import { formatThresholdNok, impliesSimplifiedFlow, simplifiedFlowThresholdNok } from "../utils/foaPartDisplay";
import { ClassificationPanel } from "../components/ClassificationPanel";
import { HomeDepartmentField } from "../components/HomeDepartmentField";
import { ResponsibleBuyerField } from "../components/ResponsibleBuyerField";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { FieldSaveErrorMessage, useFieldSaveError } from "../components/FieldSaveError";
import { ProcurementScopeToggles } from "./private/ProcurementScopeToggles";
import { resolveInclusions, resolveProcess, useStepLabel, type OrgWizardAccess } from "../wizardSteps";
import { inheritsScopeFromScheme } from "../utils/procedure";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  nextIsAction?: boolean;
  isNextLoading?: boolean;
}

export const InfoStep = ({ eventId, previousStep, nextStep, nextStepLabel, nextIsAction, isNextLoading }: Props) => {
  const { t } = useTranslation();
  const sub = useSub();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();

  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  // Estimated value and contract type exist to place a public procurement in the right part of FOA.
  // An organisation that does not run public procurements is classified PRIVATE regardless of them
  // (RegimeClassifier.derive), so the section would only ask for regulatory input that changes nothing.
  const runsPublicProcurements = useRunsPublicProcurements();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const title = state.eventData.title;
  const estimatedValue = state.eventContext.estimatedValue?.value ?? null;
  const contractNature = state.eventContext.contractNature ?? "";
  const inheritsScope = inheritsScopeFromScheme(publicContext?.procedureType);
  const governingRegulation = publicContext?.regulation ?? selectedOrg.defaultRegulation ?? null;
  // What the value alone would make this. Only a prediction: a procurement that already carries a
  // classification keeps it, because moving between the regimes discards the other one's work.
  const valueImpliesPrivate =
    !inheritsScope &&
    (!runsPublicProcurements ||
      impliesSimplifiedFlow(governingRegulation, estimatedValue != null ? Number(estimatedValue) : null));
  const isPrivate = state.eventContext.type === "Private";
  const isUnclassified = state.eventContext.type === "Unclassified";
  // Shown before the draft is classified too: the regulation and the buyer legal type are what the
  // classification is waiting for, so hiding them until after it happened put them out of reach.
  // Shown for a private procurement too: the box is where the regime is stated, and a private one
  // that has drifted above the threshold needs to say so somewhere.
  const showsClassification =
    runsPublicProcurements && !inheritsScope && (isUnclassified ? !valueImpliesPrivate : true);

  const stepLabel = useStepLabel();
  const access: OrgWizardAccess = {
    hasPublicProcurementAccess: runsPublicProcurements,
    hasProductAccess: sub.hasProductAccess,
  };
  const inclusions = resolveInclusions(state.eventData);
  const processUnresolved = resolveProcess(state.eventContext.type, access, { inclusions }) === null;
  const predictedProcess = resolveProcess(valueImpliesPrivate ? "Private" : "Public", access, { inclusions });
  const comingSteps = (predictedProcess?.steps ?? [])
    .slice(1)
    .map((step) => stepLabel(step.key))
    .join(" · ");

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const contractNatureSaveError = useFieldSaveError(eventId, "eventContext.contractNature");
  const titleFieldError = fieldError("eventData.title");
  const estimatedValueFieldError = fieldError("eventContext.estimatedValue.value");

  return (
    <Box pb="8" pt="10">
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormLabel fontWeight="bold" fontSize="md" mb="4">
          {t("General information")}
        </FormLabel>

        <FormControl isRequired mb="2" isInvalid={!!titleFieldError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Title")}</FormLabel>
            {isSaving("eventData.title") && <Spinner size="xs" />}
          </Flex>
          <Input
            type="text"
            autoFocus={title === ""}
            value={title}
            placeholder={t("E.g. Office supplies for municipality of Oslo") ?? undefined}
            onChange={(e) => updateField({ eventData: { title: e.target.value } }, { debounceMs: 300 })}
          />
          {titleFieldError ? (
            <FormErrorMessage>{formatFieldError(titleFieldError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("A short, descriptive title for the procurement")}</FormHelperText>
          )}
        </FormControl>

        <HomeDepartmentField eventId={eventId} status={state.status} homeDepartment={state.homeDepartment} />
        <ResponsibleBuyerField
          eventId={eventId}
          homeDepartmentId={state.homeDepartment?.id}
          responsiblePerson={state.responsiblePerson}
        />
      </Box>

      {runsPublicProcurements && (
        <Box borderWidth="1px" borderRadius="lg" p="5" mb="8" bg="smBackground">
          <FormLabel fontWeight="bold" fontSize="md" mb="4">
            {t("Value and regulation")}
          </FormLabel>

          <FormControl mb="6" isInvalid={!!estimatedValueFieldError}>
            <FormLabel>
              {t("Estimated value")} {isSaving("eventContext.estimatedValue") && <Spinner size="xs" />}
            </FormLabel>
            <InputGroup>
              <NumericInput
                numericValue={estimatedValue ? Number(estimatedValue) : null}
                placeholder="0"
                onNumericChange={(value) =>
                  updateField(
                    { eventContext: { estimatedValue: { value, currency: Currency.Nok } } },
                    { debounceMs: 300 },
                  )
                }
              />
              <InputRightAddon>{t("NOK")}</InputRightAddon>
            </InputGroup>
            {estimatedValueFieldError && (
              <FormErrorMessage>{formatFieldError(estimatedValueFieldError)}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl display={inheritsScope ? "none" : undefined} isInvalid={!!contractNatureSaveError}>
            <FormLabel>
              {t("Contract type")} {isSaving("eventContext.contractNature") && <Spinner size="xs" />}
            </FormLabel>
            <RadioOptionGroup
              options={CONTRACT_NATURE_OPTIONS}
              value={contractNature}
              allowDeselect
              onChange={(value) =>
                updateField({ eventContext: { contractNature: value ? (value as ContractNature) : null } })
              }
            />
            <FieldSaveErrorMessage error={contractNatureSaveError} />
          </FormControl>

          {inheritsScope && (
            <Box mt="4" fontSize="sm" color="smMuted">
              <Text>{t("Contract type and regulatory part come from the system this purchase runs under.")}</Text>
            </Box>
          )}

          {isUnclassified && valueImpliesPrivate && !inheritsScope && !estimatedValueFieldError && (
            <Box mt="4" fontSize="sm" color="smMuted">
              <Text>
                {t("Below {{threshold}} NOK — the procurement continues as a Del I procurement", {
                  threshold: formatThresholdNok(simplifiedFlowThresholdNok(governingRegulation)),
                })}
              </Text>
            </Box>
          )}

          {/* The classification box states the regime and everything that follows from it. Only the
              step preview stays out here, because it is about the wizard rather than the law. */}
          {showsClassification && !estimatedValueFieldError && <ClassificationPanel eventId={eventId} />}

          {processUnresolved && !inheritsScope && (
            <Text fontSize="xs" color="smMuted" mt="3">
              {t("Next steps")}: {comingSteps}
            </Text>
          )}
        </Box>
      )}

      {/* A public procurement's scope follows from FOA, so the choice is the private flow's alone. */}
      {/* The optional parts belong to the private flow: a public procurement keeps its own steps
          even when its value would have made a new draft private. */}
      {(isPrivate || (isUnclassified && valueImpliesPrivate)) && <ProcurementScopeToggles eventId={eventId} />}

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
        nextIsAction={nextIsAction}
        isNextLoading={isNextLoading}
      />
    </Box>
  );
};
